<!-- biome-ignore-all lint/a11y/noNoninteractiveTabindex: the tabpanel is focusable by design (APG tabs pattern) so keyboard users can reach panels without focusable content -->
<script lang="ts" setup>
  import { computed, ref, watchEffect } from 'vue';
  import { useRoute, useRouter, type LocationQueryValue } from 'vue-router';
  import { useKeyboardGroupNavigation } from '../_shared/composables/useKeyboardGroupNavigation';
  import TabItem from './TabItem/TabItem.vue';
  import type { TabsProps } from './Tabs.types';
  import {
    tabsActionsClass,
    tabsContainerClass,
    tabsHeaderClass,
    tabListClass,
  } from './Tabs.css';
  import { useId } from 'vue';
  import { useLocale } from '@tundr/i18n';

  const props = defineProps<TabsProps>();

  const emit = defineEmits<{
    'update:modelValue': [tabName: string];
  }>();

  defineOptions({
    inheritAttrs: false,
  });

  const { t } = useLocale('ds-components');

  const uid = useId();
  const resolvedTestId = computed(() => props.testId ?? `ds-tabs-${uid}`);
  const PANEL_ID = `ds-tabs-panel-${uid}`;
  const getTabId = (name: string) => `ds-tabs-tab-${uid}-${name}`;

  const route = useRoute();
  const router = useRouter();
  const tabListRef = ref<HTMLElement | null>(null);

  function queryTabToString(
    tab: LocationQueryValue | LocationQueryValue[] | undefined,
  ): string | undefined {
    if (tab == null) return undefined;
    const value = Array.isArray(tab) ? tab[0] : tab;
    return value ?? undefined;
  }

  const selectedTabName = ref<string>(
    props.modelValue ??
      queryTabToString(route.query.tab) ??
      props.firstSelectedTab,
  );

  watchEffect(() => {
    if (props.modelValue !== undefined) {
      selectedTabName.value = props.modelValue;
    } else {
      const fromRoute = queryTabToString(route.query.tab);
      selectedTabName.value = fromRoute ?? props.firstSelectedTab;
    }
  });

  const selectedTab = computed(
    () =>
      props.tabs.find((tab) => tab.name === selectedTabName.value) ??
      props.tabs[0],
  );

  const selectedTabId = computed(() =>
    selectedTab.value ? getTabId(selectedTab.value.name) : undefined,
  );

  const resolvedAriaLabel = computed(
    () => props.ariaLabel ?? t('tabs.aria_label'),
  );

  const onSelectTab = (tabName: string, onClick?: () => void) => {
    if (tabName === selectedTabName.value) return;
    selectedTabName.value = tabName;
    emit('update:modelValue', tabName);

    if (props.modelValue === undefined) {
      router.push({ query: { ...route.query, tab: tabName } });
    }

    onClick?.();
  };

  const { onKeydown: onTabListKeydown } = useKeyboardGroupNavigation({
    containerRef: tabListRef,
    role: 'tab',
    items: computed(() => props.tabs),
    getValue: (tab) => tab.name,
    selectedValue: selectedTabName,
    onSelect: (_value, tab) => onSelectTab(tab.name, tab.onClick),
  });
</script>

<template>
  <div :class="tabsContainerClass" :data-testid="resolvedTestId">
    <div :class="tabsHeaderClass">
      <div
        ref="tabListRef"
        :class="tabListClass"
        role="tablist"
        :aria-label="resolvedAriaLabel"
        :data-testid="`${resolvedTestId}-list`"
        @keydown="onTabListKeydown"
      >
        <TabItem
          v-for="tab in tabs"
          :key="tab.name"
          :label="tab.label"
          :active="tab.name === selectedTabName"
          :tab-id="getTabId(tab.name)"
          :focusable="tab.name === selectedTab?.name"
          :aria-controls="tab.name === selectedTabName ? PANEL_ID : undefined"
          @select="onSelectTab(tab.name, tab.onClick)"
        />
      </div>
      <div
        v-if="$slots.actions"
        :class="tabsActionsClass"
        :data-testid="`${resolvedTestId}-actions`"
      >
        <slot name="actions" />
      </div>
    </div>
    <div
      :id="PANEL_ID"
      role="tabpanel"
      tabindex="0"
      :aria-labelledby="selectedTabId"
      :data-testid="`${resolvedTestId}-panel`"
    >
      <component
        v-if="selectedTab"
        :is="selectedTab.component"
        :key="selectedTab.name"
        v-bind="selectedTab.props"
      />
    </div>
  </div>
</template>
